<template>
    <div class="mj__modal-announcement">
        <div v-if="!emailSubmitted">
            <h2 class="announcement-title">🎉 New Version of Mapjunction is Coming!</h2>
            <div class="announcement-gif" @click="showFullGif = true">
                <img src="/static/MapjunctionPro720pGIF_compressed.gif" alt="Mapjunction Pro Preview" />
                <div class="announcement-gif-overlay">
                    <span class="announcement-gif-text">Click to enlarge</span>
                </div>
            </div>
            <p class="announcement-text">
                We're excited to announce that a brand new version of Mapjunction is in development! 
                With improved performance, new features, and a fresh design.
            </p>
            <p class="announcement-question">
                Would you like to be notified when it launches?
            </p>
            
            <div v-if="!showEmailForm" class="announcement-buttons">
                <button class="announcement-btn announcement-btn-yes" @click="showForm">
                    Yes, notify me!
                </button>
                <button class="announcement-btn announcement-btn-no" @click="dismiss">
                    No, thanks
                </button>
            </div>

            <div v-if="showEmailForm" class="announcement-form">
                <input 
                    v-model="email" 
                    type="email" 
                    placeholder="Enter your email address"
                    class="announcement-input"
                    @keyup.enter="submitEmail"
                    :disabled="isLoading"
                >
                <div v-if="errorMessage" class="announcement-error">
                    {{ errorMessage }}
                </div>
                <div class="announcement-form-buttons">
                    <button 
                        class="announcement-btn announcement-btn-submit" 
                        @click="submitEmail"
                        :disabled="!isValidEmail || isLoading"
                    >
                        {{ isLoading ? 'Submitting...' : 'Submit' }}
                    </button>
                    <button 
                        class="announcement-btn announcement-btn-cancel" 
                        @click="cancelForm"
                        :disabled="isLoading"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>

        <div v-if="emailSubmitted" class="announcement-success">
            <h3>Thank you! 🎊</h3>
            <p>We'll notify you at <strong>{{ email }}</strong> when the new version launches.</p>
            <button class="announcement-btn announcement-btn-close" @click="dismissSuccess">
                Close
            </button>
        </div>
        
        <!-- Full-size GIF overlay -->
        <div v-if="showFullGif" class="announcement-full-gif-overlay" @click="showFullGif = false">
            <div class="announcement-full-gif-container">
                <img src="/static/MapjunctionPro720pGIF_high_quality.gif" alt="Mapjunction Pro Preview Full Size" />
                <button class="announcement-close-full" @click="showFullGif = false">✕</button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
  name: 'announcementModal',
  data() {
    return {
      showEmailForm: false,
      email: '',
      emailSubmitted: false,
      isLoading: false,
      errorMessage: '',
      showFullGif: false,
      apiEndpoint: 'https://9dtosj4ts0.execute-api.us-east-2.amazonaws.com/dev/newsletter/subscribe',
      trackingEndpoint: 'https://9dtosj4ts0.execute-api.us-east-2.amazonaws.com/dev/newsletter/track-response'
    }
  },
  computed: {
    isValidEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(this.email)
    }
  },
  methods: {
    async trackResponse(responseType, email = null) {
      try {
        // Get UTM parameters from URL
        const urlParams = new URLSearchParams(window.location.search)
        
        await fetch(this.trackingEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            response_type: responseType,
            email: email,
            
            // UTM parameters
            utm_source: urlParams.get('utm_source') || '',
            utm_medium: urlParams.get('utm_medium') || '',
            utm_campaign: urlParams.get('utm_campaign') || '',
            utm_term: urlParams.get('utm_term') || '',
            utm_content: urlParams.get('utm_content') || '',
            
            // Page and browser info
            page_url: window.location.href,
            browser_languages: navigator.languages ? navigator.languages.join(',') : (navigator.language || navigator.userLanguage || '')
          })
        })
        // Don't handle response - this is for statistics only
      } catch (error) {
        // Silently fail - tracking shouldn't block user experience
        console.log('Tracking failed:', error)
      }
    },
    showForm() {
      this.showEmailForm = true
      // Track that user clicked "Yes" - only track this once
      this.trackResponse('yesNotifyMe')
    },
    cancelForm() {
      this.showEmailForm = false
      this.email = ''
      // User said "Yes" but then cancelled - still count as interested
      // The initial "Yes" tracking already happened in showForm()
    },
    async submitEmail() {
      if (!this.isValidEmail) return
      
      // Clear previous error messages
      this.errorMessage = ''
      this.isLoading = true
      
      try {
        const response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: this.email })
        })
        
        const data = await response.json()
        
        if (response.ok) {
          // Success
          this.emailSubmitted = true
          
          // Don't track again - we already tracked "Yes" in showForm()
          // Just save the email to localStorage
          localStorage.setItem('mapjunction_announcement_seen', 'true')
          localStorage.setItem('mapjunction_announcement_email', this.email)
        } else {
          // API returned an error
          this.errorMessage = data.error || 'Failed to subscribe. Please try again.'
        }
      } catch (error) {
        // Network or other error
        console.error('Subscription error:', error)
        this.errorMessage = 'Network error. Please check your connection and try again.'
      } finally {
        this.isLoading = false
      }
    },
    dismiss() {
      // Track that user clicked "No" (declined)
      this.trackResponse('noDontNotifyMe')
      
      // Save that user has seen and dismissed the announcement
      localStorage.setItem('mapjunction_announcement_seen', 'true')
      this.$store.commit('hideModal')
      // Emit event to start intro tour
      this.$eventBus.$emit('announcementClosed')
    },
    dismissSuccess() {
      // User successfully submitted email and is closing success screen
      // No additional tracking needed - already tracked in submitEmail()
      localStorage.setItem('mapjunction_announcement_seen', 'true')
      this.$store.commit('hideModal')
      // Emit event to start intro tour
      this.$eventBus.$emit('announcementClosed')
    }
  }
}
</script>

<style scoped>
.mj__modal-announcement {
    background: white;
    padding: 40px;
    max-width: 500px;
    text-align: center;
    border-radius: 10px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    position: relative;
    margin: auto;
}

.announcement-title {
    color: #2c3e50;
    margin-bottom: 20px;
    font-size: 26px;
    font-weight: bold;
}

.announcement-gif {
    margin: 20px 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    position: relative;
    cursor: pointer;
    transition: transform 0.3s ease;
}

.announcement-gif:hover {
    transform: scale(1.02);
}

.announcement-gif img {
    width: 100%;
    height: auto;
    display: block;
}

.announcement-gif-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%);
    padding: 10px;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.announcement-gif:hover .announcement-gif-overlay {
    opacity: 1;
}

.announcement-gif-text {
    color: white;
    font-size: 14px;
    font-weight: 500;
    display: block;
    text-align: center;
}

.announcement-full-gif-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
    cursor: pointer;
}

.announcement-full-gif-container {
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    cursor: default;
}

.announcement-full-gif-container img {
    max-width: 100%;
    max-height: 90vh;
    border-radius: 8px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.announcement-close-full {
    position: absolute;
    top: -40px;
    right: 0;
    background: white;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    font-size: 24px;
    cursor: pointer;
    transition: transform 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.announcement-close-full:hover {
    transform: scale(1.1);
}

.announcement-text {
    color: #555;
    margin-bottom: 20px;
    line-height: 1.6;
    font-size: 16px;
}

.announcement-question {
    color: #333;
    margin-bottom: 25px;
    font-size: 18px;
    font-weight: 500;
}

.announcement-buttons {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-top: 20px;
}

.announcement-form {
    margin-top: 20px;
}

.announcement-input {
    width: 100%;
    padding: 12px;
    border: 2px solid #ddd;
    border-radius: 5px;
    font-size: 16px;
    margin-bottom: 15px;
    transition: border-color 0.3s;
    box-sizing: border-box;
}

.announcement-input:focus {
    outline: none;
    border-color: #4CAF50;
}

.announcement-input:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
}

.announcement-error {
    margin: 10px 0;
    padding: 10px;
    background-color: #fee;
    color: #c33;
    border-radius: 4px;
    font-size: 14px;
    border-left: 3px solid #c33;
    text-align: left;
}

.announcement-form-buttons {
    display: flex;
    justify-content: center;
    gap: 10px;
}

.announcement-btn {
    padding: 12px 30px;
    border: none;
    border-radius: 5px;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.3s;
    font-weight: 500;
}

.announcement-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.announcement-btn-yes,
.announcement-btn-submit {
    background-color: #4CAF50;
    color: white;
}

.announcement-btn-yes:hover,
.announcement-btn-submit:hover:not(:disabled) {
    background-color: #45a049;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.announcement-btn-no,
.announcement-btn-cancel {
    background-color: #6c757d;
    color: white;
}

.announcement-btn-no:hover,
.announcement-btn-cancel:hover {
    background-color: #5a6268;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.announcement-btn-close {
    background-color: #2196F3;
    color: white;
    margin-top: 20px;
}

.announcement-btn-close:hover {
    background-color: #0b7dda;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.announcement-success {
    padding: 20px;
}

.announcement-success h3 {
    color: #4CAF50;
    margin-bottom: 15px;
    font-size: 22px;
}

.announcement-success p {
    color: #555;
    margin-bottom: 20px;
    font-size: 16px;
}
</style>